import { RobotoLinkHoverUnderline } from "@/shared/components/RobotoLinkHoverUnderline";
import { formatRelativeTime, FormattedDateTime } from "@/shared/datetime";
import {
  type SessionRecord,
  type SessionTopicStatsRun,
} from "@/shared/domain/sessions";
import { CircleX, LoaderCircle } from "@/shared/foundation/icons";
import { Spinner, StatusBadge, Tooltip } from "@/shared/foundation/primitives";
import { useRelativeTick } from "@/shared/foundation/primitives/Table";
import { Goto } from "@/shared/navigation";

import styles from "./TopicStatsRunStatus.module.css";
import { useTopicStatsRunState } from "./useTopicStatsRunState";

function inFlight(run: SessionTopicStatsRun): boolean {
  return run.status === "scheduled" || run.status === "running";
}

/**
 * The header badge for the latest topic stats run: "Generating stats" while it
 * is scheduled or running, "Stats failed" once it failed, and nothing
 * otherwise.
 */
export function TopicStatsRunBadge({ run }: { run: SessionTopicStatsRun }) {
  if (inFlight(run)) {
    return (
      <StatusBadge tone="info" icon={LoaderCircle} label="Generating stats" />
    );
  }
  if (run.status === "failed") {
    return <StatusBadge tone="danger" icon={CircleX} label="Stats failed" />;
  }
  return null;
}

/** {@link TopicStatsRunBadge} for `session`, while its topic stats are shown. */
export function SessionTopicStatsBadge({
  session,
}: {
  session: SessionRecord;
}) {
  const { statsShown, run } = useTopicStatsRunState(session);
  return statsShown && run.data !== undefined ? (
    <TopicStatsRunBadge run={run.data} />
  ) : null;
}

/**
 * The Topics toolbar's line about the latest topic stats run: that it is
 * generating, since when, with a link to its invocation; or that it failed.
 * Nothing for a run that finished or never ran.
 */
export function TopicStatsRunStatus({ run }: { run: SessionTopicStatsRun }) {
  const generating = inFlight(run);
  if (!generating && run.status !== "failed") {
    return null;
  }
  const invocationLink = run.invocation_id !== null && (
    <RobotoLinkHoverUnderline
      to={Goto.createInvocationRoute(run.invocation_id)}
    >
      View invocation
    </RobotoLinkHoverUnderline>
  );
  const parts = generating
    ? [
        <span key="what" className={styles.what}>
          <Spinner aria-hidden className={styles.spinner} />
          Generating topic stats
        </span>,
        <Started key="started" started={run.started} />,
        invocationLink,
      ]
    : ["Topic stats failed", invocationLink];
  return (
    <p className={styles.status} role="status">
      {parts
        .filter((part) => part !== false)
        .map((part, index) => (
          <span key={index} className={styles.part}>
            {index > 0 && (
              <span className={styles.separator} aria-hidden>
                ·
              </span>
            )}
            {part}
          </span>
        ))}
    </p>
  );
}

/** When the run started, relative to now, with the full time on hover; or that it starts shortly. */
function Started({ started }: { started: string | null }) {
  useRelativeTick(started !== null);
  if (started === null) {
    return <>starts shortly</>;
  }
  return (
    <Tooltip>
      <Tooltip.Trigger
        delay={250}
        render={<span tabIndex={0} className={styles.started} />}
      >
        started {formatRelativeTime(started)}
      </Tooltip.Trigger>
      <Tooltip.Content>
        <FormattedDateTime value={new Date(started)} />
      </Tooltip.Content>
    </Tooltip>
  );
}
